/**
 * ClientBreadcrumb — role-correct "Your clients / [name] / [page]" breadcrumb.
 * Used on every in-client page for coordinator, caregiver, family, and admin.
 *
 * Renders up to three tiers:
 *   [clients list]  /  [client name]  /  [trail…]
 *
 * The clients-list segment and the client-name segment are always links (given
 * enough info to build them). Every entry in `trail` except the last is a link;
 * the last entry is the current page, rendered as plain text.
 *
 * PROPS
 *   clientName — string: the client's display_name. Falls back to "Client".
 *   role       — user.role: "coordinator" | "caregiver" | "agency_admin" |
 *                "family_owner" | "family_contributor" | "family_viewer".
 *                Decides the clients-list path/label and whether the name reads
 *                "[name]" (builders) or "[name] profile" (read-only audiences).
 *
 *   Client-home link (the middle segment). Provide ONE of:
 *     clientHref — explicit href for the client's home. Wins when present.
 *     clientId   — convenience: builds the role-correct home href
 *                    read-only audiences → /clients/${clientId}/
 *                    builders            → /coordinator/clients/${clientId}/edit
 *   Provide neither to render the client name as plain text (correct on the
 *   client-home page itself, where the name IS the current page).
 *
 *   trail      — optional array of { label, href? } for pages below the client
 *                home, e.g. trail={[{ label: "Communication" }]} or
 *                trail={[{ label: "Interviews", href: hubHref }, { label: "Setup" }]}.
 *                The last entry is always the current page (plain text) even if
 *                it carries an href.
 *
 *   onNavigate — optional (path) => void. When present, ALL link segments call
 *                this instead of performing a hard navigation. Pages with an
 *                unsaved-changes guard pass their handleNavAttempt here so the
 *                breadcrumb routes through the same "leave without saving?" check.
 *
 * USAGE
 *   Simple two-tier (client home page):
 *     <ClientBreadcrumb clientName={name} role={role} />
 *   Two-tier with linked name (a page directly under the client):
 *     <ClientBreadcrumb clientName={name} role={role} clientId={clientId} />
 *   Three-tier:
 *     <ClientBreadcrumb clientName={name} role={role} clientId={clientId}
 *                       trail={[{ label: "Review" }]} />
 *   Guarded page:
 *     <ClientBreadcrumb clientName={name} role={role} clientHref={hub}
 *                       trail={[{ label: "Play-through script" }]}
 *                       onNavigate={handleNavAttempt} />
 */

import React from "react";
import Wordmark from "./Wordmark.jsx";
import { Link } from "react-router-dom";
import { CLIENTS_PATH, CLIENTS_LABEL, isFamilyRole } from "../lib/roles.js";

function readOnlyRole(role) {
  return role === "caregiver" || role === "family_viewer"
      || role === "family_contributor" || role === "contributor";
}

export default function ClientBreadcrumb({
  clientName, role, clientId, clientHref, trail, onNavigate,
}) {
  const listPath    = CLIENTS_PATH[role] || "/coordinator/clients";
  const listLabel   = CLIENTS_LABEL[role] || "Your clients";
  const displayName = clientName || "Client";

  // The client "home" the name links back to. Explicit clientHref wins; else
  // derive from clientId; else the name is plain text (we're on the home page).
  let homeHref = clientHref || null;
  if (!homeHref && clientId) {
    homeHref = readOnlyRole(role)
      ? `/clients/${clientId}/`
      : `/coordinator/clients/${clientId}/edit`;
  }

  // Read-only audiences see "[name] profile"; builders see "[name]".
  const nameIsProfile =
    role === "caregiver" || role === "contributor"
    || (isFamilyRole(role) && role !== "family_owner");
  const linkLabel = nameIsProfile ? `${displayName} profile` : displayName;

  const trailItems = Array.isArray(trail) ? trail.filter(Boolean) : [];

  // A segment renders as: a guard-routed span (onNavigate), a real <Link>, or
  // plain text — depending on what it's given. One helper keeps all three tiers
  // visually and behaviourally identical.
  function Segment({ href, children, current }) {
    if (current || !href) {
      return <span style={current ? s.current : s.plain}>{children}</span>;
    }
    if (onNavigate) {
      return (
        <a
          href={href}
          onClick={(e) => { e.preventDefault(); onNavigate(href); }}
          style={s.link}
        >
          {children}
        </a>
      );
    }
    return <Link to={href} style={s.link}>{children}</Link>;
  }

  function Sep() {
    return <span style={s.sep} aria-hidden="true">/</span>;
  }

  return (
    <nav style={s.crumb} aria-label="Breadcrumb">
      {/* The app's name leads every client page, so a person who arrived by
          link — a DSP, a family member — always knows what they are in. It
          links home for the same reason the wordmark does on the list pages. */}
      <Wordmark small to={listPath} />
      <Sep />
      <Segment href={listPath}>{listLabel}</Segment>
      <Sep />
      {/* Client name: link if we have a home href AND there's a deeper page,
          or if a home href was given on a page below the client. On the client
          home itself (no href), it's the current page. */}
      {homeHref
        ? <Segment href={homeHref}>{linkLabel}</Segment>
        : <Segment current={trailItems.length === 0}>{displayName}</Segment>}

      {trailItems.map((item, i) => {
        const isLast = i === trailItems.length - 1;
        return (
          <React.Fragment key={`${item.label}-${i}`}>
            <Sep />
            <Segment href={isLast ? undefined : item.href} current={isLast}>
              {item.label}
            </Segment>
          </React.Fragment>
        );
      })}
    </nav>
  );
}

const s = {
  crumb:   { display: "flex", alignItems: "center", gap: "var(--space-2)", flexWrap: "wrap" },
  link:    { fontSize: "var(--text-sm)", color: "var(--color-text-tertiary)", textDecoration: "none" },
  sep:     { fontSize: "var(--text-sm)", color: "var(--color-text-tertiary)" },
  current: { fontSize: "var(--text-sm)", color: "var(--color-text-primary)", fontWeight: "var(--weight-medium)" },
  // "plain" = a non-current segment that has no link (rare; e.g. name with no href
  // but a trail follows). Kept distinct from current so only the true current
  // page gets the emphasized weight.
  plain:   { fontSize: "var(--text-sm)", color: "var(--color-text-primary)", fontWeight: "var(--weight-medium)" },
};
